import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import emailjs from '@emailjs/browser'
import AOS from 'aos'
import 'aos/dist/aos.css'
import Navbar from '../Navbar'
import Footer from '../page-main/footer'
import { 
  EnvelopeIcon, 
  PhoneIcon, 
  MapPinIcon,
  PaperAirplaneIcon,
  CheckIcon
} from '@heroicons/react/24/outline'

export default function Contacto() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    mensaje: ''
  })
  
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic'
    })
    
    emailjs.init("CoyMtv-5vgF3NO_Ef")
  }, [])

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const templateParams = {
        from_name: formData.nombre,
        from_email: formData.email,
        phone: formData.telefono || 'No proporcionado',
        company: formData.empresa || 'No especificada',
        message: formData.mensaje
      }

      await emailjs.send(
        'service_0gs48jg',
        'template_nyvco3w',
        templateParams
      )

      const confirmationParams = {
        email: formData.email,
        to_name: formData.nombre,
        from_name: 'Equipo Adrox',
        message: formData.mensaje
      }

      await emailjs.send(
        'service_0gs48jg',
        'template_vjikj6e',
        confirmationParams
      )

      setIsSubmitting(false)
      setSubmitStatus('success')
      
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        empresa: '',
        mensaje: ''
      })
      
      setTimeout(() => {
        setSubmitStatus(null)
      }, 8000)
      
    } catch (error) {
      console.error('Error al enviar el email:', error)
      setIsSubmitting(false)
      setSubmitStatus('error')
      
      setTimeout(() => {
        setSubmitStatus(null)
      }, 8000)
    }
  }

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: 'Teléfono',
      detail: '+51 933 534 235',
      link: 'https://wa.me/+51933534235',
      description: 'Lun - Vie, 9:00 - 18:00'
    },
    {
      icon: EnvelopeIcon,
      title: 'Email',
      detail: 'ventas@adrox.top',
      link: 'mailto:ventas@adrox.top',
      description: 'Respuesta en 24 horas'
    },
    {
      icon: MapPinIcon,
      title: 'Ubicación',
      detail: 'Lima, Perú',
      link: null,
      description: 'América Latina'
    }
  ]

  const benefits = [
    { title: 'Respuesta Rápida', desc: 'Respondemos todas las consultas en menos de 24 horas hábiles.' },
    { title: 'Asesoría Gratuita', desc: 'Primera consulta sin costo para entender tus necesidades.' },
    { title: 'Soluciones Personalizadas', desc: 'Cada proyecto es único y merece una solución a medida.' },
    { title: 'Experiencia Comprobada', desc: 'Más de 150 proyectos exitosos en América Latina.' },
  ]

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "name": "Contacto - Adrox Peru",
    "description": "Contáctanos para soluciones de desarrollo de software. Email: ventas@adrox.top, Teléfono: +51933534235",
    "url": "https://adrox.pe/contacto"
  }

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Adrox Peru",
    "email": "ventas@adrox.top",
    "telephone": "+51933534235",
    "address": { "@type": "PostalAddress", "addressLocality": "Lima", "addressCountry": "PE" }
  }

  return (
    <>
      <Helmet>
        <title>Contacto | Adrox Peru - Soluciones de Software</title>
        <meta name="description" content="Contáctanos para desarrollar tu proyecto de software en Perú. Email: ventas@adrox.top | Tel: +51 933 534 235. Respuesta garantizada en 24 horas." />
        <meta property="og:title" content="Contacto | Adrox Peru" />
        <meta property="og:description" content="Contáctanos para soluciones de desarrollo de software." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.adrox.top/contacto" />
        <link rel="canonical" href="https://www.adrox.top/contacto" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contacto | Adrox Peru" />
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div className="bg-white selection:bg-[#366D73]/20 selection:text-[#353E40]">
        <Navbar />
        
        {/* ============= HERO ============= */}
        <header className="relative pt-20 pb-16 sm:pb-20 overflow-hidden">
          {/* Dot grid */}
          <div
            className="absolute inset-0 -z-10"
            style={{
              backgroundImage: 'radial-gradient(#353E4014 1.5px, transparent 1.5px)',
              backgroundSize: '28px 28px',
            }}
          />
          <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_80%_50%_at_50%_-10%,#07F2F20d,transparent)]" />

          <div className="mx-auto max-w-5xl px-6 lg:px-8 pt-20 text-center">
            <h1
              data-aos="fade-up"
              data-aos-delay="100"
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-[#353E40] leading-[1.08]"
            >
              Estamos aquí para{' '}
              <span className="text-[#366D73]">
                ayudarte.
              </span>
            </h1>
            
            <p 
              data-aos="fade-up"
              data-aos-delay="150"
              className="mt-6 text-lg sm:text-xl text-gray-500 max-w-2xl mx-auto leading-relaxed"
            >
              ¿Tienes un proyecto en mente? Nuestro equipo está listo para convertir tus ideas en realidad. 
              Contáctanos y recibe una respuesta en menos de 24 horas.
            </p>
          </div>
        </header>

        {/* ============= CONTACT INFO CARDS ============= */}
        <section className="border-t border-gray-100 py-16">
          <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {contactInfo.map((info, index) => (
                <div
                  key={index}
                  className="group bg-white rounded-2xl border border-gray-200/60 hover:border-[#366D73]/30 hover:shadow-[0_4px_20px_-8px_rgba(54,109,115,0.15)] transition-all duration-300 p-6 sm:p-8 text-center"
                  data-aos="fade-up"
                  data-aos-delay={index * 80}
                >
                  <div className="w-12 h-12 rounded-full bg-[#366D73]/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-[#366D73]/20 transition-colors">
                    <info.icon className="h-6 w-6 text-[#366D73]" />
                  </div>
                  
                  <h3 className="text-sm font-semibold text-[#353E40] mb-2">{info.title}</h3>
                  
                  {info.link ? (
                    <a 
                      href={info.link}
                      className="text-lg font-bold text-[#366D73] hover:text-[#3B8C8C] transition-colors block mb-1"
                      target={info.link.startsWith('http') ? '_blank' : '_self'}
                      rel="noopener noreferrer"
                    >
                      {info.detail}
                    </a>
                  ) : (
                    <p className="text-lg font-bold text-[#353E40] mb-1">{info.detail}</p>
                  )}
                  <p className="text-sm text-gray-500">{info.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ============= FORM + BENEFITS ============= */}
        <section className="border-t border-gray-100 py-16 sm:py-20">
          <div className="mx-auto max-w-full px-4 sm:px-6 lg:px-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              
              {/* Left - Benefits */}
              <div data-aos="fade-right">
                <p className="text-xs font-semibold text-[#366D73] tracking-[0.2em] uppercase mb-4">
                  ¿Por qué elegirnos?
                </p>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#353E40] mb-8">
                  Confianza que <span className="text-[#366D73]">respalda</span>
                </h2>
                <div className="space-y-6">
                  {benefits.map((item) => (
                    <div key={item.title} className="flex gap-4">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-6 h-6 rounded-full bg-[#366D73]/10 flex items-center justify-center">
                          <CheckIcon className="w-3.5 h-3.5 text-[#366D73]" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold text-[#353E40]">{item.title}</h3>
                        <p className="text-sm text-gray-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right - Form */}
              <div data-aos="fade-left">
                <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-gray-200/60 p-6 sm:p-8">
                  <h3 className="text-xl font-bold text-[#353E40] mb-6">Envíanos un mensaje</h3>
                  
                  <div className="space-y-5">
                    <div>
                      <label htmlFor="nombre" className="block text-sm font-medium text-[#353E40] mb-1.5">
                        Nombre completo *
                      </label>
                      <input
                        type="text"
                        id="nombre"
                        name="nombre"
                        required
                        value={formData.nombre}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[#353E40] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#366D73]/30 focus:border-[#366D73] transition-all duration-200 text-sm"
                        placeholder="Juan Pérez"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-[#353E40] mb-1.5">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[#353E40] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#366D73]/30 focus:border-[#366D73] transition-all duration-200 text-sm"
                        placeholder="juan@empresa.com"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="telefono" className="block text-sm font-medium text-[#353E40] mb-1.5">
                          Teléfono
                        </label>
                        <input
                          type="tel"
                          id="telefono"
                          name="telefono"
                          value={formData.telefono}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[#353E40] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#366D73]/30 focus:border-[#366D73] transition-all duration-200 text-sm"
                          placeholder="+51 999 999 999"
                        />
                      </div>
                      <div>
                        <label htmlFor="empresa" className="block text-sm font-medium text-[#353E40] mb-1.5">
                          Empresa
                        </label>
                        <input
                          type="text"
                          id="empresa"
                          name="empresa"
                          value={formData.empresa}
                          onChange={handleChange}
                          className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[#353E40] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#366D73]/30 focus:border-[#366D73] transition-all duration-200 text-sm"
                          placeholder="Nombre de tu empresa"
                        />
                      </div>
                    </div>

                    <div>
                      <label htmlFor="mensaje" className="block text-sm font-medium text-[#353E40] mb-1.5">
                        Mensaje *
                      </label>
                      <textarea
                        id="mensaje"
                        name="mensaje"
                        required
                        rows="4"
                        value={formData.mensaje}
                        onChange={handleChange}
                        className="w-full px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[#353E40] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#366D73]/30 focus:border-[#366D73] transition-all duration-200 text-sm resize-none"
                        placeholder="Cuéntanos sobre tu proyecto..."
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#366D73] hover:bg-[#3B8C8C] text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <PaperAirplaneIcon className="h-4 w-4" />
                          Enviar Mensaje
                        </>
                      )}
                    </button>

                    {submitStatus === 'success' && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                        <p className="text-green-700 text-sm text-center font-medium">
                          ✅ ¡Mensaje enviado exitosamente! <br/>
                          <span className="text-green-600 text-xs">Revisa tu email para ver la confirmación automática.</span>
                        </p>
                      </div>
                    )}

                    {submitStatus === 'error' && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                        <p className="text-red-600 text-sm text-center font-medium">
                          ❌ Error al enviar el mensaje. Por favor intenta nuevamente o contáctanos por WhatsApp.
                        </p>
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  )
}